import React, { useMemo } from 'react'
import { TaskActivityBase } from './TaskActivityBase'
import { TaskActivity } from '@core/entities/task/TaskActivity'
import moment from 'moment'
import { Collapse } from '@mui/material'
import { TransitionGroup } from 'react-transition-group'
import { VirtualizedTemplateAnswers } from '@elements/Questions/VirtualizedTemplateAnswers'
import { Text } from '@fundamentals'
import { Card } from '@common/material'
import { TaskPropertiesDiffCard } from '../TaskPropertiesDiffCard'

type TaskActivityAuditResultProps = {
  activity: TaskActivity
  bgColor?: string
  isModal?: boolean
}

export const TaskActivityAuditResult: React.FC<
  TaskActivityAuditResultProps
> = ({ activity, bgColor, isModal }) => {
  const type = activity.getAuditActionStepType()

  const plainAnswers = useMemo(() => {
    return (
      activity.auditResult.metadata.taskPostValuesData?.stepData?.stepAnswers?.answers?.map(
        (questionGroup) => questionGroup.toPlain(),
      ) || []
    )
  }, [activity])

  return (
    <TaskActivityBase
      name={activity.getTitle()}
      date={moment(activity.date).format('MMM D, YYYY HH:mm')}
      user={activity.auditResult?.user}
      active
      type={type}
      color={activity.getStepTypeColor(type)}
      bgColor={bgColor}
      isModal={isModal}
    >
      <TransitionGroup>
        {activity.auditResult.isAction('TASK_CANCELLED') &&
          !!activity.auditResult.metadata.taskCancelledNotes && (
            <Collapse>
              <Card sx={{ p: 2 }}>
                <Text variant='body2' data-test='task-cancelled-notes'>
                  {activity.auditResult.metadata.taskCancelledNotes}
                </Text>
              </Card>
            </Collapse>
          )}
        {activity.auditResult.isAction('TASK_CHANGES_REQUESTED') &&
          !!activity.auditResult.metadata.taskRequestChangesComment && (
            <Collapse>
              <Card sx={{ p: 2 }}>
                <Text variant='body2' data-test='request-changes-comment'>
                  {activity.auditResult.metadata.taskRequestChangesComment}
                </Text>
              </Card>
            </Collapse>
          )}
        {['TASK_REASSIGNED', 'TASK_UPDATED'].includes(
          activity.auditResult.action,
        ) && (
          <Collapse>
            <TaskPropertiesDiffCard
              preValues={activity.auditResult.metadata.taskPreValuesData}
              postValues={activity.auditResult.metadata.taskPostValuesData}
            />
          </Collapse>
        )}
        {activity.auditResult.metadata.taskReassignedNotes && (
          <Collapse>
            <Card sx={{ p: 2, mt: 1.5 }}>
              <Text variant='body2' data-test='task-reassigned-notes'>
                {activity.auditResult.metadata.taskReassignedNotes}
              </Text>
            </Card>
          </Collapse>
        )}
        {activity.auditResult.metadata.taskReopenReason && (
          <Collapse>
            <Card sx={{ p: 2, mt: 1.5 }}>
              <Text variant='body2' data-test='task-reopen-reason'>
                {activity.auditResult.metadata.taskReopenReason}
              </Text>
            </Card>
          </Collapse>
        )}
        {['TASK_WORK_STEP_SUBMITTED', 'TASK_CREATED', 'TASK_APPROVED'].includes(
          activity.auditResult.action,
        ) && (
          <Collapse>
            <VirtualizedTemplateAnswers
              questions={plainAnswers}
              warnings={[]}
            />
          </Collapse>
        )}
      </TransitionGroup>
    </TaskActivityBase>
  )
}
